import React, { useEffect, useState, useContext } from "react";
import { Button, Container, Row } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { abpnClient, IdentityServerKnownClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import { MedicalEducation } from "../Physician/Models/MedicalEducationModel";
import Physician from "../Physician/Models/Physician";
import PhysicianCreation, { PhysicianName } from "../Physician/Models/PhysicianCreation";
import MedicalEducationCard from "./MedicalEducationCard";
import PersonalEmailCard from "./PersonalEmailCard";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import { AbpnSpinner } from './../Shared/Components/AbpnSpinner';
import { PersonalInformationCard } from './PersonalInformationCard';
import { ApplicantInformation } from '../Shared/Components/ApplicantInformation';
import { Address } from "../Physician/Models/Address";
import SearchPhysicianModel from "./Models/SearchPhysicianModel";
import ActivateUser from "./Models/ActivateUser";
import ActionResultMessage from './../Shared/Components/ActionResultMessage';
import HeaderWithTitle from './../Shared/Components/HeaderWithTitle';
import { ApplicationPaths } from "../../Services/Auth/AuthConstants";
import CreatePhysicianModel from './../Physician/Models/CreatePhysicianModel';

type TParams = { data: SearchPhysicianModel, examinationId: number };

export function PersonalInformation() {
    const authContext = useContext(AuthContext);
    const location = useLocation<TParams>();
    const data = location.state.data;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [physicianModel, setPhysicianModel] = useState<Physician>(null);
    const [newPhysician, setNewPhysician] = useState<PhysicianCreation>(null);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [runValidation, setRunValidation] = useState<boolean>(false);
    const [isMedicalEducationFormValid, setIsMedicalEducationFormValid] = useState<boolean>(null);
    const [isPersonalInfoFormValid, setIsPersonalInfoFormValid] = useState<boolean>(null);
    const [isAddressFormValid, setIsAddressFormValid] = useState<boolean>(null);
    const [isNewEmailValid, setIsNewEmailValid] = useState<boolean>(null);
    const [userModel, setUserModel] = useState<ActivateUser>(null);

    useEffect(() => {
        const getFormLookupItems = () => {
            abpnClient.get<Physician>(`/api/Profile/GetPhysicianFormLookupItems`, null)
                .then(result => {
                    setPhysicianModel(result);
                    setIsLoading(false);
                });
        }

        const createNewPhysician = () => {
            var physician = new PhysicianCreation();
            physician.name.lastName = data.lastName;
            physician.dateOfBirth = data.dateOfBirth;
            physician.ssn4 = data.ssn4;

            setNewPhysician(physician);
        }
        createNewPhysician();
        getFormLookupItems();
    }, []);

    const validateAndSave = () => {
        let newUserModel = new ActivateUser(data, newPhysician.email);
        newUserModel.registerClientId = IdentityServerKnownClient.physicianPortal;
        setUserModel(newUserModel);
        setRunValidation(true);
    }

    useEffect(() => {
        const CreatePhysician = async () => {
            setRunValidation(false);
            setIsLoading(true);

            let model: CreatePhysicianModel = {
                physician: newPhysician,
                examinationId: location.state.examinationId
            }

            await abpnClient.post<PhysicianCreation>("/api/profile/CreatePhysician", model, null);
            await authContext.register(userModel);
            setIsLoading(false);
            setShowAlert(true);
        }

        if (isMedicalEducationFormValid
            && isPersonalInfoFormValid
            && isAddressFormValid
            && isNewEmailValid) {
            CreatePhysician();
        }
    }, [isMedicalEducationFormValid, isPersonalInfoFormValid, isAddressFormValid, isNewEmailValid]);

    const updateMedicalEducation = (medicalEducation: MedicalEducation) => {
        setRunValidation(false);
        var updated = deepCopy(newPhysician);
        updated.medicalEducation = medicalEducation;
        setNewPhysician(updated);
    }

    const updatePersonalEmail = (email: string) => {
        setRunValidation(false);
        var updated = deepCopy(newPhysician);
        updated.email = email;
        setIsNewEmailValid(null);
        setNewPhysician(updated);
    }

    const updatePhysicianName = (name: PhysicianName) => {
        setRunValidation(false);
        var updated = deepCopy(newPhysician);
        updated.name = name;
        setNewPhysician(updated);
    }

    const updateContactAddress = (addresses: Array<Address>) => {
        var contactAddress = addresses.find(_ => _.isContact);
        var updated = deepCopy(newPhysician);
        updated.address = contactAddress;
        setNewPhysician(updated);
        setIsAddressFormValid(contactAddress.isValid);
    }

    if (isLoading) return <AbpnSpinner />

    if (showAlert) return <ActionResultMessage title={`Account Activated`} subTitle={null} alertVariant={"pp-alert-success"} message={`Please check your ${userModel?.email} email account and follow instructions to create your new password within 24 hours and continue your examination application.`} />

    return (
        <Container>
            <HeaderWithTitle
                title={"Provide Information"}
                subTitle={"Please provide the following information to activate your ABPN Physician Portal account."} />

            <PersonalInformationCard
                physicianData={data}
                runValidation={runValidation}
                isFormValid={isPersonalInfoFormValid}
                updatePersonalInformation={(name: PhysicianName) => updatePhysicianName(name)}
                updatePersonalInformationValidation={(isPersonalInformationValid: boolean) => setIsPersonalInfoFormValid(isPersonalInformationValid)} />

            <ApplicantInformation
                isNewPhysician={true}
                fullName={null}
                addresses={physicianModel.addresses.addresses}
                emailAddress={null}
                runValidation={runValidation}
                states={physicianModel.addresses.states}
                countries={physicianModel.addresses.countries}
                isSectionOpen={true}
                onSave={(addresses: Array<Address>) => updateContactAddress(addresses)}
                onUpdate={() => setRunValidation(false)} />

            <PersonalEmailCard
                runValidation={runValidation}
                isEmailFormValid={isNewEmailValid}
                userModel={userModel}
                updatePersonalEmail={(email: string) => updatePersonalEmail(email)}
                updateEmailValidation={(isEmailFormValid: boolean) => setIsNewEmailValid(isEmailFormValid)} />

            <MedicalEducationCard
                runValidation={runValidation}
                isFormValid={isMedicalEducationFormValid}
                medicalEducationModel={physicianModel.medicalEducationModel}
                medicalEducation={newPhysician.medicalEducation}
                updateMedicalEducation={(medicalEducation: MedicalEducation) => updateMedicalEducation(medicalEducation)}
                updateMedicalEducationValidation={(isMedicalEducationValid: boolean) => setIsMedicalEducationFormValid(isMedicalEducationValid)} />

            <Row noGutters className="align-items-end">
                <div>
                    <Button className="mr-3 mt-3 mb-4" variant="pp-primary" type="button" onClick={() => validateAndSave()}>Activate Account</Button>
                    <Link to={ApplicationPaths.SearchForAccount}>
                        <Button className="mr-3 mt-3 mb-4 pp-outline-primary-white-button" variant={"outline-pp-primary"} >Search Account Again</Button>
                    </Link>
                    <Link to={ApplicationPaths.Login}>
                        <Button className="mt-3 mb-4 pp-outline-white-button" variant={"outline-dark"} >Cancel</Button>
                    </Link>
                </div>
            </Row>
        </Container>
    );
}

export default PersonalInformation;